import { PLANNER_INTENT_CLASSES, type PlannerIntentClass } from './planner-intent-class';
import {
  OBJECTIVE_MEASURE_BRIDGE_TRANSFORMS,
  OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY,
  type ObjectiveContributionContract,
  type ObjectiveMeasureBridge,
} from './objective-value-bridge';
import {
  validateExecutiveAnswerContract,
  type ExecutiveAnswerContract,
} from './executive-answer-contract';
import { validateCapabilityRequirements } from './governed-capability-requirement-validation';
// Requirement-unit vocabulary lives in a sibling module and is re-exported below.
import {
  EVIDENCE_STALE_BEHAVIORS,
  GOVERNED_CAPABILITY_OUTPUT_ENVELOPES,
  type GovernedCapabilityOutputEnvelope,
  CAPABILITY_REQUIREMENT_VALUE_TYPES,
  type CapabilityRequirementValueType,
  CAPABILITY_REQUIREMENT_UNITS,
  type CapabilityRequirementUnit,
  CAPABILITY_REQUIREMENT_UNIT_SPELLINGS,
  capabilityRequirementUnitAdmits,
} from './governed-capability-manifest-requirement-units';
export {
  EVIDENCE_STALE_BEHAVIORS,
  GOVERNED_CAPABILITY_OUTPUT_ENVELOPES,
  type GovernedCapabilityOutputEnvelope,
  CAPABILITY_REQUIREMENT_VALUE_TYPES,
  type CapabilityRequirementValueType,
  CAPABILITY_REQUIREMENT_UNITS,
  type CapabilityRequirementUnit,
  CAPABILITY_REQUIREMENT_UNIT_SPELLINGS,
  capabilityRequirementUnitAdmits,
};
import { isGovernedReferenceExportDeclaration, type CapabilityStructuredToolInputV1, type CapabilityTrustedReferenceContractV1, type ConsumerReferenceRequirement, type GovernedReferenceExportDeclaration } from './governed-argument-export';
import {
  RETRIEVAL_FAILURE_MODES,
  RETRIEVAL_LIFECYCLE_ENTRIES,
  type RetrievalFailureMode,
  type RetrievalLifecycleEntry,
  type RetrievalCapabilityDeclaration,
} from './governed-capability-manifest-retrieval';
import type { MetricRollupGrain } from '../../metric-registry.contracts';
// #7322 increment 2 size-law split — this scenario vocabulary moved to a
// sibling file to make room for the new `metricGrain` field below without
// growing this touched file; same content-identical, re-exported pattern as
// the #5850 split above — see governed-capability-manifest-scenario.ts.
import {
  scenarioSpecGovernsIntent,
  type ScenarioDefaultBand,
  type ScenarioSpec,
} from './governed-capability-manifest-scenario';
export {
  scenarioSpecGovernsIntent,
  type ScenarioDefaultBand,
  type ScenarioSpec,
};

export const GOVERNED_CAPABILITY_DOMAINS = frozenTuple([
  'company',
  'strategy',
  'org',
  'finance',
  'people',
  'hr',
  'operations',
  'commercial',
  'technology',
  'transportation',
  'warehouse',
  'capacity',
  'project',
  'benchmark',
  'risk',
  'legal',
  'supply_chain',
  'manufacturing',
  'custom',
] as const);

export type GovernedCapabilityDomain = (typeof GOVERNED_CAPABILITY_DOMAINS)[number];

export const GOVERNED_CAPABILITY_STATUSES = frozenTuple(['available', 'unavailable'] as const);

export type GovernedCapabilityStatus = (typeof GOVERNED_CAPABILITY_STATUSES)[number];

export const GOVERNED_CAPABILITY_INTENT_KINDS = frozenTuple([
  'read',
  'scenario',
  'simulation',
  'optimization',
  'comparison',
  'narration_input',
] as const);

export type GovernedCapabilityIntentKind = (typeof GOVERNED_CAPABILITY_INTENT_KINDS)[number];

export const GOVERNED_CAPABILITY_INTENT_ROUTING_VISIBILITIES = frozenTuple([
  'routing_visible',
  'admin_introspection_only',
] as const);

export type GovernedCapabilityIntentRoutingVisibility =
  (typeof GOVERNED_CAPABILITY_INTENT_ROUTING_VISIBILITIES)[number];

/**
 * #5341 — WHO PRODUCES the metric an intent anchors, DECLARED as capability
 * metadata rather than inferred by the manager from a module name.
 *
 * `owning_module` (the default when the field is absent) — the intent is
 * authored by the module that owns the metric's governed computation.
 * `registry_projection` — the intent is GENERATED from a metric registry row
 * rather than authored by the metric's owning module. It answers the same
 * `answerAnchors.metricKey` in the same answer shape, so a projection is a
 * second producer of one governed identity. When an owning-module intent for
 * the same metric key and shape is executable, the catalog keeps the owning
 * module's intent selectable and projects the registry projection out of the
 * selectable set, naming the intent that superseded it.
 *
 * This is a property of the CAPABILITY, reviewable next to the intent it
 * describes; the manager reads the declaration and never a module identifier.
 */
export const GOVERNED_CAPABILITY_METRIC_PRODUCER_ROLES = frozenTuple([
  'owning_module',
  'registry_projection',
] as const);

export type GovernedCapabilityMetricProducerRole =
  (typeof GOVERNED_CAPABILITY_METRIC_PRODUCER_ROLES)[number];

/**
 * #5341 — WHAT AN ANSWER COMPARES THE METRIC AGAINST, declared on the intent.
 *
 * `answerAnchors.shape` says an answer is a `comparison`; it does not say a
 * comparison against WHAT, and two comparisons of one metric against different
 * references are different questions with different answers. "How did EBITDA
 * move?" (prior period) and "How far is EBITDA from target?" (target) share a
 * metric key and a shape and answer neither for the other.
 *
 * So the basis is DECLARED, in a closed set, next to the intent it describes,
 * and it is part of the governed metric identity the catalog reasons about. It
 * is never derived from reader wording, from an intent id, from a module or
 * capability name, or from the metric key: those are exactly the inferences a
 * declaration exists to replace.
 *
 * Absent ⇒ `none` — the answer states the metric itself and compares it with
 * nothing, which is what every lookup does.
 */
export const GOVERNED_CAPABILITY_COMPARISON_BASES = frozenTuple([
  'none',
  'prior_period',
  'target',
  'plan',
  // #5714 Child 1 — a conservative-versus-base (or any named-scenario-versus-scenario)
  // comparison is declared as `scenario`, never mislabeled `plan`, `target` or
  // `prior_period`: none of those describe two declared bands of one selected promise set.
  'scenario',
] as const);

export type GovernedCapabilityComparisonBasis =
  (typeof GOVERNED_CAPABILITY_COMPARISON_BASES)[number];

export const GOVERNED_CAPABILITY_SIDE_EFFECT_CLASSES = frozenTuple([
  'read_only',
  'simulation',
  'draft_write',
  'governed_write',
] as const);

export type GovernedCapabilitySideEffectClass =
  (typeof GOVERNED_CAPABILITY_SIDE_EFFECT_CLASSES)[number];

export const GOVERNED_CAPABILITY_PROOF_QUALITIES = frozenTuple([
  'observed',
  'approved',
  'observed_or_approved',
  'inferred',
  'unknown',
] as const);

export type GovernedCapabilityProofQuality = (typeof GOVERNED_CAPABILITY_PROOF_QUALITIES)[number];

export const GOVERNED_CAPABILITY_FAILURE_MODES = frozenTuple([
  'missing_evidence',
  'stale_evidence',
  'capability_unavailable',
  'permission_denied',
  'ambiguous_scope',
  'invalid_input',
  'missing_function_taxonomy',
  'no_such_entity',
  'scenario_opt_in_required',
  'unsupported_intent',
] as const);

export type GovernedCapabilityFailureMode = (typeof GOVERNED_CAPABILITY_FAILURE_MODES)[number];

/**
 * The governed unit a supplied SPELLING stands for, or null when the spelling is
 * not one this contract declares.
 *
 * Reading a declared spelling table is the boundary where an external format
 * becomes a typed value — the same table `capabilityRequirementUnitAdmits`
 * already checks against, so there is no second unit vocabulary. It resolves
 * nothing by inference and has no near-miss fallback.
 */
export function canonicalCapabilityRequirementUnit(
  suppliedUnit: string | null | undefined
): CapabilityRequirementUnit | null {
  const supplied =
    typeof suppliedUnit === 'string' ? suppliedUnit.trim().toLowerCase() : null;
  if (supplied === null || supplied === '') return null;
  return (
    CAPABILITY_REQUIREMENT_UNITS.find((unit) =>
      CAPABILITY_REQUIREMENT_UNIT_SPELLINGS[unit].includes(supplied)
    ) ?? null
  );
}

export type CapabilityRequirement = {
  requirementId: string;
  label: string;
  required: boolean;
  valueType?: CapabilityRequirementValueType;
  /** Closed engine-owned choices, valid only for string requirements. */
  allowedValues?: readonly string[];
  /**
   * The governed unit this input's value is stated in. Omitted = unitless, and
   * a unitless requirement admits only a supply that states no unit.
   */
  unit?: CapabilityRequirementUnit;
  modelInterpretedScenarioNumeric?: Readonly<{ minimum: number; maximum: number }>;
  requiredEvidenceKeysWhenPresent?: string[];
  /**
   * No-opt-in law (defaults + disclosure beat refusal): when a REQUIRED input
   * is absent from the planner's call, the capability validator resolves it to
   * this documented default instead of blocking `invalid_input`, and records
   * the disclosure sentence on the validated call so the producer can render
   * the defaults it ran under. The disclosure must be digit-free prose (the
   * numbers-are-fixed law owns every digit). Absent = the requirement keeps
   * its strict blocking behavior.
   */
  defaultWhenAbsent?: {
    value: boolean | number | string;
    disclosure: string;
  };
  /** Numeric and object reference requirements are distinct contracts. */
  trustedReferenceContract?: CapabilityTrustedReferenceContractV1;
  referenceRequirement?: ConsumerReferenceRequirement;
};

export type EvidenceRef = {
  evidenceRefId: string;
  sourceClass: string;
  sourceId: string;
  asOf: string;
  claim?: string | null;
};

export type AssumptionRef = {
  assumptionId: string;
  label: string;
  rationale?: string | null;
};

export type GapRef = {
  gapId: string;
  label: string;
  severity: 'low' | 'medium' | 'high';
  blocksAnswer: boolean;
};

export type EvidencePolicy = {
  maxAgeHours: number;
  staleBehavior: (typeof EVIDENCE_STALE_BEHAVIORS)[number];
};

export type GovernedCapabilityEvidenceRequirement = {
  evidenceKey: string;
  sourceClass: string;
  freshness: EvidencePolicy;
  required: boolean;
  proofQuality?: GovernedCapabilityProofQuality;
};

/**
 * The SHAPE an answer to a governed intent takes. Determines what "leading with the
 * answer" means for that intent: a lookup leads with one figure, a bridge leads with
 * the two endpoints and the reconciling items, a decomposition leads with the parts,
 * a projection leads with the horizon value, a comparison leads with the difference.
 */
export const GOVERNED_CAPABILITY_ANSWER_SHAPES = [
  'lookup',
  'bridge',
  'decomposition',
  'projection',
  'comparison',
] as const;

export type GovernedCapabilityAnswerShape =
  (typeof GOVERNED_CAPABILITY_ANSWER_SHAPES)[number];

/**
 * #4008 lane DF1 (diagnosis FIX-2). Authored per intent, consumed by the asked-focus
 * guard once the narration seam carries the reader's question (FIX-1, blocked on FCF).
 */
export type GovernedCapabilityAnswerAnchors = {
  /**
   * Governed metric keys the answer must bind IN THE LEAD. At least one must be bound
   * for the answer to count as on-question. Every key must exist in the module's
   * emitted metric surface — an anchor naming a metric the module cannot emit is a
   * declared-but-hollow capability, which is the defect this contract exists to expose.
   */
  metricKeys: string[];
  /**
   * #5041 - anchors this intent WOULD bind if the evidence supported them, but whose
   * absence must not make the whole intent unofferable.
   *
   * The EBITDA bridge is the case that forced this: its walk from operating income to
   * EBITDA is fully servable from any approved snapshot, but its anchors also named
   * year-over-year deltas, which a single-as-of snapshot cannot produce. Requiring EVERY
   * anchor meant the whole bridge went unoffered and the reader got a generic block
   * instead of the walk the module can actually do.
   *
   * A key listed here is still a declared anchor - it stays in `metricKeys`, so the focus
   * guard is unchanged - but servability is judged on the REQUIRED remainder, and an
   * optional key the evidence cannot support publishes as a named gap. Never a value.
   */
  optionalMetricKeys?: string[];
  /**
   * Reader-facing words that name the asked subject. Used to check that the lead talks
   * about the right thing, not merely that a number appeared. Executive vocabulary
   * only — never a copy of a benchmark question.
   */
  lexemes: string[];
  shape: GovernedCapabilityAnswerShape;
  /**
   * Set when the module CANNOT currently emit the metrics this intent's answer needs.
   * An honest named gap here is a passing state: it tells the guard to require a
   * named-gap clause on the asked subject instead of a bound figure, which is the
   * difference between an honest block and a confidently wrong answer.
   */
  namedGap?: string;
};

/**
 * #4777 amendment 1 — THE NAMED GAP IS A READER-FACING DECLARATION, CHECKED
 * WHERE IT IS AUTHORED. `answerAnchors.namedGap` reaches the narrator VERBATIM
 * via the focus fence's repair instruction, so a declaration naming a function
 * or source file makes the reader-facing fences reject the prose it asked for.
 * A downstream classifier that silently substituted prose would be a second
 * prose-admission authority hiding the defect; this check FAILS THE MANIFEST
 * instead. Returns the error suffix for an unsafe declaration, or `null`; it
 * NEVER rewrites the value.
 */
const NAMED_GAP_INTERNAL_VOCABULARY: readonly string[] = frozenTuple([
  'decision_agent',
  'decision-agent',
  'briefs_agent',
  'briefs-agent',
  'strategy_workspace_truth_gap',
  'evidence_pack',
] as const);

/** A module source file name — how an engineering note about code is written. */
const NAMED_GAP_SOURCE_FILE_RE = /\b[A-Za-z0-9_.-]+\.(?:ts|tsx|js|mjs|cjs|json)\b/;

/** A bare `camelCaseIdentifier` run — a function or symbol name, not prose. */
const NAMED_GAP_CODE_IDENTIFIER_RE = /\b[a-z][A-Za-z0-9]*[A-Z][A-Za-z0-9]*\b/;

export function unsafeNamedGapDeclarationReason(value: unknown): string | null {
  const declaration = typeof value === 'string' ? value.trim() : '';
  if (!declaration) return null;
  if (NAMED_GAP_SOURCE_FILE_RE.test(declaration)) {
    return 'namedGap_names_source_file';
  }
  if (NAMED_GAP_CODE_IDENTIFIER_RE.test(declaration)) {
    return 'namedGap_names_code_identifier';
  }
  const lowered = declaration.toLowerCase();
  if (NAMED_GAP_INTERNAL_VOCABULARY.some((marker) => lowered.includes(marker))) {
    return 'namedGap_carries_internal_vocabulary';
  }
  return null;
}

// #5168 retrieval capability contract — moved to
// `governed-capability-manifest-retrieval.ts` (#5841 size-law extraction);
// re-exported here (via the import above) so every existing import path is
// unaffected.
export type { RetrievalFailureMode, RetrievalLifecycleEntry, RetrievalCapabilityDeclaration };
export { RETRIEVAL_FAILURE_MODES, RETRIEVAL_LIFECYCLE_ENTRIES };

export type GovernedCapabilityIntent = {
  intentId: string;
  referenceExports?: readonly GovernedReferenceExportDeclaration[];
  label: string;
  description: string;
  kind: GovernedCapabilityIntentKind;
  sideEffectClass?: GovernedCapabilitySideEffectClass;
  // Present when an intent belongs in admin/module introspection but must not be
  // advertised to the runtime router until its governed proof is certified.
  routingVisibility?: GovernedCapabilityIntentRoutingVisibility;
  examples: string[];
  // Descriptive vocabulary; never tool control flow.
  aliases?: string[];
  // Maintainer-only notes, excluded from planner projection.
  plannerGuidance?: string[];
  requiredInputs: CapabilityRequirement[];
  requiredEvidenceDomains: GovernedCapabilityDomain[];
  requiredEvidenceKeys: string[];
  /** Absent producer role means `owning_module`. */
  metricProducerRole?: GovernedCapabilityMetricProducerRole;
  /** Absent comparison basis means `none`. */
  comparisonBasis?: GovernedCapabilityComparisonBasis;
  // The industry-agnostic planner intent classes this intent can serve. Lets the
  // runtime ask `manifestSupportsIntent(manifest, 'provenance_drilldown')` instead
  // of matching industry vocabulary in the raw question. Optional for backward
  // compatibility; absent ⇒ the intent advertises no first-class archetype.
  servesIntentClasses?: PlannerIntentClass[];
  /**
   * #4008 lane DF1 (diagnosis FIX-2) — what an ANSWER to this intent must actually
   * contain, declared as DATA next to the intent it serves.
   *
   * `examples` say how a reader might PHRASE the ask. `answerAnchors` say what the
   * answer must LEAD WITH for the ask to count as answered: which governed metric
   * keys must be bound in the lead, which reader lexemes name the subject, and what
   * shape the answer takes. This is the source of truth the asked-focus guard will
   * read once the narration seam carries the reader's question — authoring it here
   * keeps the industry-scaling law (industry = data, never code) and puts the
   * anchor set under review next to the capability it describes.
   *
   * Declaring anchors changes NO runtime behaviour today. It is inert data until
   * the guard consuming it lands.
   */
  answerAnchors?: GovernedCapabilityAnswerAnchors;
  /**
   * #4619 Slice 1 — the richer, versioned, wording-independent answer contract this
   * intent's answers must satisfy (obligations, semantic number roles, recommendation
   * policy, section policy). EXTENDS `answerAnchors`, deprecating nothing: anchors keep
   * serving the asked-focus guard as-is, and migration onto this contract is a later
   * slice. Optional; absent ⇒ manifest behaviour is completely unchanged. Inert data
   * until the answer-context compiler and outbound validator consume it.
   */
  executiveAnswerContract?: ExecutiveAnswerContract;
  contribution?: ObjectiveContributionContract;
  /**
   * #5329 — the governed restatement this intent COMPUTES, relating the measure
   * it answers to another measure by a constant it also publishes. Optional;
   * absent — which is every intent but the few that actually compute such a
   * walk — changes nothing. It is not a contribution: it adds no value to a
   * package, it states how two measures relate so a contribution governed in one
   * of them can be compared against a target stated in the other.
   */
  measureBridge?: ObjectiveMeasureBridge;
  /**
   * This intent's job IS to assemble the governed decision/activation pack —
   * owner, control plan, verification metric, approval — WITHOUT minting a new
   * number.
   *
   * #4008. The bridge previously decided to build a pack from four substrings in
   * the reader's question (`decision pack`, `decision package`, `approval`,
   * `decision needs`). An intent whose declared purpose is exactly that pack
   * therefore did nothing when the reader phrased the ask in its own manifest
   * vocabulary ("what's the activation plan to realize the recovery?"). What a
   * governed intent DOES is a property of the intent, not of the reader's
   * wording. Declaring it here keeps that decision in data.
   *
   * An intent that declares this must NOT also declare `contribution`: a pack
   * assembler mints no number, and owning a contribution contract is what makes
   * two intents indistinguishable to the value-gap source selector.
   */
  assemblesDecisionPack?: boolean;
  /** #5841 BAND-AUTHORITY P0 — the capability-owned map from declared requirements to
   * structured tool-input fields, incl. which fields receive a validated band identity
   * (`bandRef`, proven equal to the contract at mint time). The manager transports, never
   * defaults. Absent => plain scalar `arguments`. See `CapabilityStructuredToolInputV1`. */
  structuredToolInput?: CapabilityStructuredToolInputV1;
  /**
   * #5168 — present only on an intent that is a lawful read-only evidence
   * retrieval. Absent ⇒ this intent carries no retrieval behaviour and the
   * gap compiler never offers `retrieve_evidence` for it. Declaring this
   * changes no runtime behaviour by itself — it is inert data until the
   * retrieval selection rule (apps/api ask-helix/manager-runtime/retrieval)
   * reads it.
   */
  retrieval?: RetrievalCapabilityDeclaration;
  /** Specific declared lifecycle tool; absent retains the capability default. */
  toolName?: string;
  /**
   * #7322 increment 2 — GRAIN-AWARE VALIDATION CARRIER.
   *
   * `requestedGrain`: the exact {dimension, rollup} pairs THIS intent's bound
   * operation actually executes at (its own period rollup, and its own
   * enterprise/member dimension rollup — see `semantic-metric-query.
   * operations.ts`). `declaredGrains`: the metric's own full declared grain
   * set, copied verbatim from the governed registry's
   * `MetricRegistryEntry.aggregationRules` (metric-registry.contracts.ts).
   *
   * `CapabilityValidator` rejects a call whenever a `requestedGrain` entry
   * names a {dimension, rollup} pair `declaredGrains` does not contain — a
   * plan naming a rollup or dimension the registry does not support. Absent
   * (the whole field omitted, as every intent left it before #7322) is the
   * unchanged, pre-existing behaviour: nothing here regresses a manifest that
   * declares none of it.
   */
  metricGrain?: {
    requestedGrain: MetricRollupGrain[];
    declaredGrains: MetricRollupGrain[];
  };
};

export type SolverPolicy = {
  optimizeFor: 'risk_adjusted_impact' | 'maximum_impact' | 'minimum_risk';
  requireProofQualityForBackbone: GovernedCapabilityProofQuality;
  allowInferredForecastsAsCloser: boolean;
};

export type GovernedCapabilityConfidenceModel = {
  model: string;
  minimumProofQuality: GovernedCapabilityProofQuality;
};

export type GovernedCapabilityManifest = {
  capabilityId: string;
  toolName: string;
  domain: GovernedCapabilityDomain;
  status: GovernedCapabilityStatus;
  displayName: string;
  description: string;
  executiveQuestions: string[];
  businessIntents: string[];
  intents: GovernedCapabilityIntent[];
  supportedIntentKinds: GovernedCapabilityIntentKind[];
  requiredEvidence: GovernedCapabilityEvidenceRequirement[];
  formulasUsed: string[];
  scenarioInputs: ScenarioSpec[];
  outputEnvelope: GovernedCapabilityOutputEnvelope;
  numericBindingsProduced: string[];
  confidenceModel: GovernedCapabilityConfidenceModel;
  failureModes: GovernedCapabilityFailureMode[];
  permissions: string[]; // #5631 - [] is lawful least privilege
  canBeMultiModule: boolean;
  sideEffectClass: GovernedCapabilitySideEffectClass;
  solverPolicy?: SolverPolicy | null;
  unavailableReason?: string | null;
};

export type GovernedCapabilityManifestValidation = {
  ok: boolean;
  errors: string[];
};

const OBJECTIVE_CONTRIBUTION_UNITS = frozenTuple(['USD', 'PCT', 'BPS', 'COUNT'] as const);
const OBJECTIVE_CONTRIBUTION_SIGN_CONVENTIONS = frozenTuple([
  'positive_improves_metric',
  'negative_improves_metric',
] as const);
const OBJECTIVE_CONTRIBUTION_PUBLISHABILITY = frozenTuple([
  'additive',
  'candidate_only',
  'non_additive',
  'cash_only',
] as const);
const OBJECTIVE_CONTRIBUTION_CLAIM_TYPES = frozenTuple([
  'p_and_l',
  'cash',
  'run_rate',
  'capacity',
  'risk',
] as const);
const APPROVED_OVERLAP_DIMENSION_KEYS = frozenTuple(
  Object.values(OVERLAP_DIMENSION_KEYS_BY_METRIC_FAMILY).flat(),
);

// ---------------------------------------------------------------------------
// Intent-class routing (shared substrate)
// ---------------------------------------------------------------------------
// The ~20 thin SG&A / transport / cash lever modules share five stock intent
// suffixes. Rather than repeat `servesIntentClasses` on every one of those
// modules (the cross-cutting-concern-per-module anti-pattern), the substrate owns
// the generic suffix → archetype mapping ONCE here. Bespoke modules (finance, org,
// carrier, pricing, workforce) declare `servesIntentClasses` explicitly per intent
// because their archetype semantics are module-specific. `manifestSupportsIntent`
// is the structured routing predicate the runtime asks instead of matching industry
// vocabulary in the raw question.
export const STANDARD_LEVER_INTENT_CLASS_BY_SUFFIX: ReadonlyArray<
  readonly [string, readonly PlannerIntentClass[]]
> = [
  // #4008 FWF2 — THE DECLARATION FLIP. `_size_contribution` is the stock-lever
  // suffix for "size THIS module's contribution to the objective". That is the
  // CONTRIBUTION class, never the AUTHORITY class: sizing a contribution to the
  // objective is not authority to become it (see `objective-canvas-authority.ts`).
  // This one line moves the three doors that carry no explicit declaration and
  // rely on this fallback — `capex_discipline_size_contribution`,
  // `working_capital_size_contribution`, `professional_fees_size_contribution`.
  ['_size_contribution', ['value_gap_contribution']],
  ['_read_posture', ['metric_readout']],
  ['_addressable_readout', ['metric_readout']],
  ['_assumption_drilldown', ['provenance_drilldown']],
  ['_overlap_boundary', ['provenance_drilldown']],
];

// Pure evidence / approval gates carry no first-class question archetype: they are
// runtime gates, not answerable intents. They legitimately serve zero intent classes.
export function isPureGateIntent(intent: Pick<GovernedCapabilityIntent, 'intentId'>): boolean {
  return /_evidence_gate$|_approval_gate$/.test(intent.intentId);
}

// Resolve the planner intent classes an intent serves: an explicit manifest field
// wins; otherwise fall back to the shared stock-lever suffix mapping; otherwise none.
export function deriveServesIntentClasses(
  intent: Pick<GovernedCapabilityIntent, 'intentId' | 'servesIntentClasses'>
): PlannerIntentClass[] {
  if (Array.isArray(intent.servesIntentClasses) && intent.servesIntentClasses.length > 0) {
    return [...intent.servesIntentClasses];
  }
  for (const [suffix, classes] of STANDARD_LEVER_INTENT_CLASS_BY_SUFFIX) {
    if (intent.intentId.endsWith(suffix)) {
      return [...classes];
    }
  }
  return [];
}

export function intentServesIntentClass(
  intent: Pick<GovernedCapabilityIntent, 'intentId' | 'servesIntentClasses'>,
  intentClass: PlannerIntentClass
): boolean {
  return deriveServesIntentClasses(intent).includes(intentClass);
}

// ---------------------------------------------------------------------------
// #4008 FWF — CONTRIBUTE vs BECOME. The seam that keeps the class split honest.
// ---------------------------------------------------------------------------
// The flagship-well fix splits the old `value_gap_bridge` into an AUTHORITY class
// (only the objective owner declares it, and it alone admits the composite canvas
// that prints OBJECTIVE / REQUIRED UPLIFT / VERDICT) and a CONTRIBUTION class,
// `value_gap_contribution` — what the ~24 module `*_size_contribution` /
// `*_contribution_bridge` intents actually mean.
//
// Two different questions are asked of an intent across the runtime, and before
// the split they were spelled identically:
//
//   "may this turn BECOME the objective bridge?"  -> intentServesIntentClass(intent, 'value_gap_bridge')
//   "may this call CONTRIBUTE to the bridge?"     -> THIS predicate
//
// The second is asked wherever the composite SELECTS, VALIDATES or schema-arms
// its lever calls. Those sites must keep matching contribution intents once the
// declarations move, or the composite fails closed on its own levers — the exact
// inverse of the defect and a considerably worse one. Asking this predicate there
// instead of the bare class check is what turns the declaration flip into a data
// change rather than a rewrite.
//
// BEHAVIOUR TODAY IS IDENTICAL to the bare check it replaces: no intent declares
// `value_gap_contribution` until the declarations move, so this returns exactly
// what `intentServesIntentClass(intent, 'value_gap_bridge')` returned. This is
// pre-arming, not a behaviour change.
export function intentContributesToObjectiveValueGap(
  intent: Pick<GovernedCapabilityIntent, 'intentId' | 'servesIntentClasses'>
): boolean {
  const classes = deriveServesIntentClasses(intent);
  return (
    classes.includes('value_gap_bridge') ||
    classes.includes('value_gap_contribution')
  );
}

// Structured routing predicate: does this capability advertise any intent serving
// the requested archetype? Routing asks this instead of keyword-matching the question.
export function manifestSupportsIntent(
  manifest: Pick<GovernedCapabilityManifest, 'intents'>,
  intentClass: PlannerIntentClass
): boolean {
  return manifest.intents.some((intent) => intentServesIntentClass(intent, intentClass));
}

export function validateGovernedCapabilityManifest(
  value: unknown
): GovernedCapabilityManifestValidation {
  const errors: string[] = [];
  const manifest = isRecord(value) ? value : null;
  if (!manifest) {
    return { ok: false, errors: ['manifest_object_required'] };
  }

  requireString(manifest, 'capabilityId', errors);
  requireString(manifest, 'toolName', errors);
  requireEnum(manifest, 'domain', GOVERNED_CAPABILITY_DOMAINS, errors);
  requireEnum(manifest, 'status', GOVERNED_CAPABILITY_STATUSES, errors);
  requireString(manifest, 'displayName', errors);
  requireString(manifest, 'description', errors);
  requireNonEmptyStringArray(manifest, 'executiveQuestions', errors);
  requireNonEmptyStringArray(manifest, 'businessIntents', errors);
  validateCapabilityIntents(manifest.intents, errors);
  requireNonEmptyEnumArray(
    manifest,
    'supportedIntentKinds',
    GOVERNED_CAPABILITY_INTENT_KINDS,
    errors
  );
  requireNonEmptyEnumArray(manifest, 'failureModes', GOVERNED_CAPABILITY_FAILURE_MODES, errors);
  requireStringArray(manifest, 'permissions', errors); // #5631 - [] is lawful least privilege
  requireBoolean(manifest, 'canBeMultiModule', errors);
  requireEnum(manifest, 'sideEffectClass', GOVERNED_CAPABILITY_SIDE_EFFECT_CLASSES, errors);
  requireEnum(manifest, 'outputEnvelope', GOVERNED_CAPABILITY_OUTPUT_ENVELOPES, errors);

  const evidence = manifest.requiredEvidence;
  if (!Array.isArray(evidence) || evidence.length === 0) {
    errors.push('requiredEvidence_required');
  } else {
    evidence.forEach((entry, index) => {
      const requirement = isRecord(entry) ? entry : null;
      if (!requirement) {
        errors.push(`requiredEvidence[${index}]_object_required`);
        return;
      }
      if (
        typeof requirement.evidenceKey !== 'string' ||
        requirement.evidenceKey.trim().length === 0
      ) {
        errors.push(`requiredEvidence[${index}].evidenceKey_required`);
      }
      if (
        typeof requirement.sourceClass !== 'string' ||
        requirement.sourceClass.trim().length === 0
      ) {
        errors.push(`requiredEvidence[${index}].sourceClass_required`);
      }
      if (typeof requirement.required !== 'boolean') {
        errors.push(`requiredEvidence[${index}].required_required`);
      }
      const freshness = isRecord(requirement.freshness) ? requirement.freshness : null;
      if (!freshness || !Number.isFinite(freshness.maxAgeHours)) {
        errors.push(`requiredEvidence[${index}].freshness.maxAgeHours_required`);
      }
      if (!freshness || typeof freshness.staleBehavior !== 'string') {
        errors.push(`requiredEvidence[${index}].freshness.staleBehavior_required`);
      } else if (!isOneOf(freshness.staleBehavior, EVIDENCE_STALE_BEHAVIORS)) {
        errors.push(`requiredEvidence[${index}].freshness.staleBehavior_invalid`);
      }
      if (
        hasOwn(requirement, 'proofQuality') &&
        (typeof requirement.proofQuality !== 'string' ||
          !isOneOf(requirement.proofQuality, GOVERNED_CAPABILITY_PROOF_QUALITIES))
      ) {
        errors.push(`requiredEvidence[${index}].proofQuality_invalid`);
      }
    });
  }

  requireStringArray(manifest, 'formulasUsed', errors);
  requireStringArray(manifest, 'numericBindingsProduced', errors);
  validateScenarioInputs(
    manifest.scenarioInputs,
    errors,
    declaredCapabilityIntentIds(manifest.intents)
  );
  validateRequirementValueTypeConsistency(manifest, errors);

  const confidenceModel = isRecord(manifest.confidenceModel) ? manifest.confidenceModel : null;
  if (!confidenceModel) {
    errors.push('confidenceModel_required');
  } else {
    requireString(confidenceModel, 'model', errors, 'confidenceModel.model');
    requireEnum(
      confidenceModel,
      'minimumProofQuality',
      GOVERNED_CAPABILITY_PROOF_QUALITIES,
      errors,
      'confidenceModel.minimumProofQuality'
    );
  }

  return { ok: errors.length === 0, errors: Array.from(new Set(errors)) };
}

function requireString(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  errorKey = key
): void {
  const value = readPath(record, key);
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${errorKey}_required`);
  }
}

function requireEnum<T extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: T,
  errors: string[],
  errorKey = key
): void {
  const value = readPath(record, key);
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${errorKey}_required`);
    return;
  }
  if (!isOneOf(value, values)) {
    errors.push(`${errorKey}_invalid`);
  }
}

function requireBoolean(record: Record<string, unknown>, key: string, errors: string[]): void {
  if (typeof readPath(record, key) !== 'boolean') {
    errors.push(`${key}_required`);
  }
}

function requireNonEmptyStringArray(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  errorKey = key
): void {
  const value = readPath(record, key);
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    value.some((item) => typeof item !== 'string' || item.trim().length === 0)
  ) {
    errors.push(`${errorKey}_required`);
  }
}

function requireStringArray(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  errorKey = key
): void {
  const value = readPath(record, key);
  if (!Array.isArray(value)) {
    errors.push(`${errorKey}_required`);
    return;
  }
  value.forEach((item, index) => {
    if (typeof item !== 'string' || item.trim().length === 0) {
      errors.push(`${errorKey}[${index}]_required`);
    }
  });
}

function requireNonEmptyEnumArray<T extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: T,
  errors: string[]
): void {
  const value = readPath(record, key);
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${key}_required`);
    return;
  }
  value.forEach((item, index) => {
    if (typeof item !== 'string' || item.trim().length === 0) {
      errors.push(`${key}[${index}]_required`);
    } else if (!isOneOf(item, values)) {
      errors.push(`${key}[${index}]_invalid`);
    }
  });
}

function declaredCapabilityIntentIds(value: unknown): ReadonlySet<string> {
  if (!Array.isArray(value)) return new Set<string>();
  const intentIds = new Set<string>();
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const intentId = typeof entry.intentId === 'string' ? entry.intentId.trim() : '';
    if (intentId.length > 0) intentIds.add(intentId);
  }
  return intentIds;
}

function validateScenarioInputs(
  value: unknown,
  errors: string[],
  declaredIntentIds: ReadonlySet<string>
): void {
  if (!Array.isArray(value)) {
    errors.push('scenarioInputs_required');
    return;
  }
  value.forEach((entry, index) => {
    const scenario = isRecord(entry) ? entry : null;
    if (!scenario) {
      errors.push(`scenarioInputs[${index}]_object_required`);
      return;
    }
    requireString(scenario, 'scenarioKind', errors, `scenarioInputs[${index}].scenarioKind`);
    if (typeof scenario.optInRequired !== 'boolean') {
      errors.push(`scenarioInputs[${index}].optInRequired_required`);
    }
    // Optional field: absent ⇒ only the `scenarioKind === intentId` match applies.
    if (hasOwn(scenario, 'intentIds')) {
      requireStringArray(scenario, 'intentIds', errors, `scenarioInputs[${index}].intentIds`);
      validateScenarioIntentIdBindings(
        scenario.intentIds,
        errors,
        `scenarioInputs[${index}].intentIds`,
        declaredIntentIds
      );
    }
    validateCapabilityRequirements(requirementValidationInput({
      value: scenario.requiredInputs,
      errors,
      errorPath: `scenarioInputs[${index}].requiredInputs`,
    }));
    validateScenarioDefaultBands(
      scenario.defaultBands,
      errors,
      `scenarioInputs[${index}].defaultBands`
    );
  });
}

/**
 * A scenario-to-intent binding must point at a real intent OF THE SAME
 * CAPABILITY, once. An unknown id is a dead binding: the spec's parameters
 * silently never reach the intent the author meant, and — worse — the safety
 * check that reads the same rule silently sees no governing spec. Fail the
 * manifest by name instead (#4736).
 */
function validateScenarioIntentIdBindings(
  value: unknown,
  errors: string[],
  errorKey: string,
  declaredIntentIds: ReadonlySet<string>
): void {
  if (!Array.isArray(value)) return;
  const seen = new Set<string>();
  value.forEach((entry, index) => {
    if (typeof entry !== 'string') return;
    const intentId = entry.trim();
    // Empty/blank entries are already reported by requireStringArray.
    if (intentId.length === 0) return;
    if (seen.has(intentId)) {
      errors.push(`${errorKey}[${index}]_duplicate`);
      return;
    }
    seen.add(intentId);
    if (!declaredIntentIds.has(intentId)) {
      errors.push(`${errorKey}[${index}]_unknown_intent`);
    }
  });
}

function validateScenarioDefaultBands(
  value: unknown,
  errors: string[],
  errorKey: string
): void {
  // Optional field: absent ⇒ the scenario is simply not commissionable.
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    errors.push(`${errorKey}_array_required`);
    return;
  }
  value.forEach((entry, index) => {
    const band = isRecord(entry) ? entry : null;
    if (!band) {
      errors.push(`${errorKey}[${index}]_object_required`);
      return;
    }
    requireString(band, 'requirementId', errors, `${errorKey}[${index}].requirementId`);
    const values = band.values;
    if (!Array.isArray(values) || values.length === 0) {
      errors.push(`${errorKey}[${index}].values_required`);
    } else if (!values.every((item) => typeof item === 'number' && Number.isFinite(item))) {
      errors.push(`${errorKey}[${index}].values_must_be_finite_numbers`);
    } else if (
      typeof band.recommendedValue === 'number' &&
      !values.includes(band.recommendedValue)
    ) {
      errors.push(`${errorKey}[${index}].recommendedValue_must_be_in_values`);
    }
  });
}

/**
 * The intent's OPTIONAL routing declarations: the planner archetypes it serves,
 * how it produces the metric it anchors (#5341), and what its answer compares
 * that metric against (#5341). Each is optional and each is closed — declaring
 * one admits only a member of its set, and declaring none is a valid statement
 * of the defaults.
 */
function validateIntentRoutingDeclarations(
  intent: Record<string, unknown> & { servesIntentClasses?: unknown },
  index: number,
  errors: string[]
): void {
  if (hasOwn(intent, 'metricProducerRole')) {
    requireEnum(
      intent,
      'metricProducerRole',
      GOVERNED_CAPABILITY_METRIC_PRODUCER_ROLES,
      errors,
      `intents[${index}].metricProducerRole`
    );
  }
  if (hasOwn(intent, 'comparisonBasis')) {
    requireEnum(
      intent,
      'comparisonBasis',
      GOVERNED_CAPABILITY_COMPARISON_BASES,
      errors,
      `intents[${index}].comparisonBasis`
    );
  }
  if (!hasOwn(intent, 'servesIntentClasses')) return;
  const serves = intent.servesIntentClasses;
  if (!Array.isArray(serves)) {
    errors.push(`intents[${index}].servesIntentClasses_required`);
    return;
  }
  serves.forEach((entry, entryIndex) => {
    if (typeof entry !== 'string' || !isOneOf(entry, PLANNER_INTENT_CLASSES)) {
      errors.push(`intents[${index}].servesIntentClasses[${entryIndex}]_invalid`);
    }
  });
}

function validateCapabilityIntents(value: unknown, errors: string[]): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push('intents_required');
    return;
  }
  const seen = new Set<string>();
  value.forEach((entry, index) => {
    const intent = isRecord(entry) ? entry : null;
    if (!intent) {
      errors.push(`intents[${index}]_object_required`);
      return;
    }
    requireString(intent, 'intentId', errors, `intents[${index}].intentId`);
    if (intent.referenceExports !== undefined && (!Array.isArray(intent.referenceExports)
      || !intent.referenceExports.every(isGovernedReferenceExportDeclaration)
      || new Set(intent.referenceExports.map((row) => row.exportKey)).size !== intent.referenceExports.length)) {
      errors.push(`intents[${index}].referenceExports_invalid`);
    }
    requireString(intent, 'label', errors, `intents[${index}].label`);
    requireString(intent, 'description', errors, `intents[${index}].description`);
    requireEnum(intent, 'kind', GOVERNED_CAPABILITY_INTENT_KINDS, errors, `intents[${index}].kind`);
    if (hasOwn(intent, 'toolName')) {
      requireString(intent, 'toolName', errors, `intents[${index}].toolName`);
    }
    if (hasOwn(intent, 'sideEffectClass')) {
      requireEnum(
        intent,
        'sideEffectClass',
        GOVERNED_CAPABILITY_SIDE_EFFECT_CLASSES,
        errors,
        `intents[${index}].sideEffectClass`
      );
    }
    if (hasOwn(intent, 'routingVisibility')) {
      requireEnum(
        intent,
        'routingVisibility',
        GOVERNED_CAPABILITY_INTENT_ROUTING_VISIBILITIES,
        errors,
        `intents[${index}].routingVisibility`
      );
    }
    const intentId = typeof intent.intentId === 'string' ? intent.intentId : '';
    if (intentId) {
      if (seen.has(intentId)) {
        errors.push(`intents[${index}].intentId_duplicate`);
      }
      seen.add(intentId);
    }
    requireNonEmptyStringArray(intent, 'examples', errors);
    if (hasOwn(intent, 'aliases')) {
      requireNonEmptyStringArray(
        intent,
        'aliases',
        errors,
        `intents[${index}].aliases`
      );
    }
    if (hasOwn(intent, 'plannerGuidance')) {
      requireNonEmptyStringArray(
        intent,
        'plannerGuidance',
        errors,
        `intents[${index}].plannerGuidance`
      );
    }
    validateCapabilityRequirements(requirementValidationInput({
      value: intent.requiredInputs,
      errors,
      errorPath: `intents[${index}].requiredInputs`,
    }));
    requireNonEmptyEnumArray(
      intent,
      'requiredEvidenceDomains',
      GOVERNED_CAPABILITY_DOMAINS,
      errors
    );
    requireStringArray(intent, 'requiredEvidenceKeys', errors);
    validateIntentRoutingDeclarations(intent, index, errors);
    if (hasOwn(intent, 'answerAnchors')) {
      const anchors = intent.answerAnchors as
        | Partial<GovernedCapabilityAnswerAnchors>
        | undefined;
      const path = `intents[${index}].answerAnchors`;
      if (!anchors || typeof anchors !== 'object' || Array.isArray(anchors)) {
        errors.push(`${path}_invalid`);
      } else {
        const namedGap =
          typeof anchors.namedGap === 'string' && anchors.namedGap.trim().length > 0;
        const metricKeys = Array.isArray(anchors.metricKeys)
          ? anchors.metricKeys
          : null;
        if (!metricKeys || metricKeys.some((key) => typeof key !== 'string' || !key.trim())) {
          errors.push(`${path}.metricKeys_invalid`);
        } else if (metricKeys.length === 0 && !namedGap) {
          // An intent with no bindable metric and no named gap is a hollow
          // declaration — exactly the shape that let declared-but-unimplemented
          // intents pass as capabilities.
          errors.push(`${path}.metricKeys_required_without_namedGap`);
        }
        const lexemes = Array.isArray(anchors.lexemes) ? anchors.lexemes : null;
        if (
          !lexemes ||
          lexemes.length === 0 ||
          lexemes.some((word) => typeof word !== 'string' || !word.trim())
        ) {
          errors.push(`${path}.lexemes_required`);
        }
        if (
          typeof anchors.shape !== 'string' ||
          !isOneOf(anchors.shape, GOVERNED_CAPABILITY_ANSWER_SHAPES)
        ) {
          errors.push(`${path}.shape_invalid`);
        }
        if (hasOwn(anchors, 'namedGap') && !namedGap) {
          errors.push(`${path}.namedGap_invalid`);
        }
        // The declaration travels verbatim into the narrator's repair prompt,
        // so an engineering note about the module's own implementation is a
        // manifest defect, caught at authoring time and named loudly rather
        // than swallowed by a downstream text classifier.
        const unsafeNamedGap = unsafeNamedGapDeclarationReason(anchors.namedGap);
        if (unsafeNamedGap) {
          errors.push(`${path}.${unsafeNamedGap}`);
        }
      }
    }
    if (hasOwn(intent, 'executiveAnswerContract')) {
      // Pass-through to the shared contract validator (#4619 Slice 1). Absent field ⇒
      // zero behaviour change; present field ⇒ its own validator's errors, prefixed
      // with this intent's path, in the same error style as every other check here.
      const contractResult = validateExecutiveAnswerContract(intent.executiveAnswerContract);
      contractResult.errors.forEach((error) => {
        errors.push(`intents[${index}].executiveAnswerContract.${error}`);
      });
    }
    if (hasOwn(intent, 'retrieval')) {
      validateRetrievalCapabilityDeclaration(
        intent.retrieval,
        errors,
        `intents[${index}].retrieval`
      );
    }
    if (hasOwn(intent, 'contribution')) {
      validateObjectiveContributionContract(
        intent.contribution,
        errors,
        `intents[${index}].contribution`
      );
    }
    if (hasOwn(intent, 'measureBridge')) {
      validateObjectiveMeasureBridge(
        intent.measureBridge,
        errors,
        `intents[${index}].measureBridge`
      );
    }
  });
}

function validateRetrievalCapabilityDeclaration(
  value: unknown,
  errors: string[],
  errorPath: string
): void {
  const declaration = isRecord(value) ? value : null;
  if (!declaration) {
    errors.push(`${errorPath}_object_required`);
    return;
  }
  requireNonEmptyStringArray(declaration, 'evidenceKeys', errors, `${errorPath}.evidenceKeys`);
  if (!Array.isArray(declaration.scopeFilters)) {
    errors.push(`${errorPath}.scopeFilters_required`);
  }
  requireString(declaration, 'sourceClass', errors, `${errorPath}.sourceClass`);
  requireNonEmptyStringArray(declaration, 'permissions', errors, `${errorPath}.permissions`);
  if (declaration.sideEffectClass !== 'read_only') {
    errors.push(`${errorPath}.sideEffectClass_must_be_read_only`);
  }
  requireString(declaration, 'outputSchemaRef', errors, `${errorPath}.outputSchemaRef`);
  const freshness = isRecord(declaration.freshness) ? declaration.freshness : null;
  if (!freshness || !Number.isFinite(freshness.maxAgeHours)) {
    errors.push(`${errorPath}.freshness.maxAgeHours_required`);
  }
  if (
    typeof declaration.boundedResultLimit !== 'number' ||
    !Number.isFinite(declaration.boundedResultLimit) ||
    declaration.boundedResultLimit <= 0
  ) {
    errors.push(`${errorPath}.boundedResultLimit_must_be_positive`);
  }
  const failureModes = declaration.failureModes;
  if (
    !Array.isArray(failureModes) ||
    failureModes.length === 0 ||
    failureModes.some(
      (mode) => typeof mode !== 'string' || !isOneOf(mode, RETRIEVAL_FAILURE_MODES)
    )
  ) {
    errors.push(`${errorPath}.failureModes_required`);
  }
  requireEnum(
    declaration,
    'lifecycleEntry',
    RETRIEVAL_LIFECYCLE_ENTRIES,
    errors,
    `${errorPath}.lifecycleEntry`
  );
}

function validateObjectiveContributionContract(
  value: unknown,
  errors: string[],
  errorPath: string
): void {
  const contribution = isRecord(value) ? value : null;
  if (!contribution) {
    errors.push(`${errorPath}_object_required`);
    return;
  }

  const targetMetricRef = isRecord(contribution.targetMetricRef)
    ? contribution.targetMetricRef
    : null;
  if (!targetMetricRef) {
    errors.push(`${errorPath}.targetMetricRef_required`);
  } else {
    requireString(targetMetricRef, 'metricId', errors, `${errorPath}.targetMetricRef.metricId`);
    requireString(
      targetMetricRef,
      'registryVersion',
      errors,
      `${errorPath}.targetMetricRef.registryVersion`
    );
  }

  requireString(contribution, 'contributionClaimId', errors, `${errorPath}.contributionClaimId`);
  requireString(contribution, 'numericBindingKey', errors, `${errorPath}.numericBindingKey`);
  requireEnum(contribution, 'unit', OBJECTIVE_CONTRIBUTION_UNITS, errors, `${errorPath}.unit`);
  requireEnum(
    contribution,
    'signConvention',
    OBJECTIVE_CONTRIBUTION_SIGN_CONVENTIONS,
    errors,
    `${errorPath}.signConvention`
  );
  requireEnum(
    contribution,
    'publishability',
    OBJECTIVE_CONTRIBUTION_PUBLISHABILITY,
    errors,
    `${errorPath}.publishability`
  );
  requireStringArray(contribution, 'formulaRefs', errors, `${errorPath}.formulaRefs`);
  requireStringArray(
    contribution,
    'requiredEvidenceKeys',
    errors,
    `${errorPath}.requiredEvidenceKeys`
  );

  const impactClaim = isRecord(contribution.requiredImpactClaim)
    ? contribution.requiredImpactClaim
    : null;
  if (!impactClaim) {
    errors.push(`${errorPath}.requiredImpactClaim_required`);
    return;
  }
  requireString(
    impactClaim,
    'metricKey',
    errors,
    `${errorPath}.requiredImpactClaim.metricKey`
  );
  requireNonEmptyEnumArray(
    impactClaim,
    'claimTypes',
    OBJECTIVE_CONTRIBUTION_CLAIM_TYPES,
    errors
  );
  validateOverlapDimensionKeyArray(
    impactClaim.overlapDimensionKeys,
    errors,
    `${errorPath}.requiredImpactClaim.overlapDimensionKeys`
  );
}

/**
 * #5329 — a measure bridge is validated on FORM only: the two measures it
 * relates, a transform from the closed vocabulary, and the identity of the
 * governed claim carrying the constant. Whether the restatement is true is the
 * declaring module's approved formula's business, proven by the claim the
 * result actually binds — never by anything asserted here.
 */
function validateObjectiveMeasureBridge(
  value: unknown,
  errors: string[],
  errorPath: string
): void {
  const bridge = isRecord(value) ? value : null;
  if (!bridge) {
    errors.push(`${errorPath}_object_required`);
    return;
  }
  for (const field of ['fromMeasureRef', 'toMeasureRef'] as const) {
    const ref = isRecord(bridge[field]) ? (bridge[field] as Record<string, unknown>) : null;
    if (!ref) {
      errors.push(`${errorPath}.${field}_required`);
      continue;
    }
    requireString(ref, 'metricId', errors, `${errorPath}.${field}.metricId`);
  }
  requireEnum(
    bridge,
    'transform',
    OBJECTIVE_MEASURE_BRIDGE_TRANSFORMS,
    errors,
    `${errorPath}.transform`
  );
  requireString(bridge, 'constantClaimId', errors, `${errorPath}.constantClaimId`);
  requireString(bridge, 'constantBindingKey', errors, `${errorPath}.constantBindingKey`);
  requireString(bridge, 'constantMetricKey', errors, `${errorPath}.constantMetricKey`);
}

function validateOverlapDimensionKeyArray(
  value: unknown,
  errors: string[],
  errorPath: string
): void {
  if (!Array.isArray(value) || value.length === 0) {
    errors.push(`${errorPath}_required`);
    return;
  }
  value.forEach((entry, index) => {
    if (typeof entry !== 'string' || !isOneOf(entry, APPROVED_OVERLAP_DIMENSION_KEYS)) {
      errors.push(`${errorPath}[${index}]_invalid`);
    }
  });
}

function validateRequirementValueTypeConsistency(
  manifest: Record<string, unknown>,
  errors: string[]
): void {
  const seen = new Map<string, CapabilityRequirementValueType>();
  // #5042 — unit consistency is checked on ABSENCE too, unlike valueType. An
  // omitted unit is a positive claim that the input is unitless, so the same
  // requirement id declared `percent` on one intent and unitless on another is
  // a contradiction: one of the two would authenticate `8` from "8 basis
  // points" while the other rejects it.
  const seenUnits = new Map<string, CapabilityRequirementUnit | null>();

  const observeRequirements = (value: unknown, errorPath: string): void => {
    if (!Array.isArray(value)) return;
    value.forEach((entry, index) => {
      const requirement = isRecord(entry) ? entry : null;
      if (!requirement) return;
      const requirementId =
        typeof requirement.requirementId === 'string'
          ? requirement.requirementId.trim()
          : '';
      if (!requirementId) return;
      const unit =
        typeof requirement.unit === 'string' &&
        isOneOf(requirement.unit, CAPABILITY_REQUIREMENT_UNITS)
          ? requirement.unit
          : null;
      if (seenUnits.has(requirementId) && seenUnits.get(requirementId) !== unit) {
        errors.push(`${errorPath}[${index}].unit_conflict`);
      } else {
        seenUnits.set(requirementId, unit);
      }
      const valueType =
        typeof requirement.valueType === 'string' &&
        isOneOf(requirement.valueType, CAPABILITY_REQUIREMENT_VALUE_TYPES)
          ? requirement.valueType
          : null;
      if (!valueType) return;
      const prior = seen.get(requirementId);
      if (prior && prior !== valueType) {
        errors.push(`${errorPath}[${index}].valueType_conflict`);
        return;
      }
      seen.set(requirementId, valueType);
    });
  };

  const intents = Array.isArray(manifest.intents) ? manifest.intents : [];
  intents.forEach((entry, index) => {
    const intent = isRecord(entry) ? entry : null;
    observeRequirements(
      intent?.requiredInputs,
      `intents[${index}].requiredInputs`
    );
  });

  const scenarioInputs = Array.isArray(manifest.scenarioInputs)
    ? manifest.scenarioInputs
    : [];
  scenarioInputs.forEach((entry, index) => {
    const scenario = isRecord(entry) ? entry : null;
    observeRequirements(
      scenario?.requiredInputs,
      `scenarioInputs[${index}].requiredInputs`
    );
  });
}

function requirementValidationInput(input: Readonly<{
  value: unknown;
  errors: string[];
  errorPath: string;
}>) {
  return {
    ...input,
    dependencies: {
      valueTypes: CAPABILITY_REQUIREMENT_VALUE_TYPES,
      units: CAPABILITY_REQUIREMENT_UNITS,
      isRecord,
      hasOwn,
      isOneOf,
      requireString,
      requireEnum,
      requireNonEmptyStringArray,
      requireStringArray,
    },
  };
}

function isOneOf<T extends readonly string[]>(value: string, values: T): value is T[number] {
  return (values as readonly string[]).includes(value);
}

function frozenTuple<const T extends readonly string[]>(values: T): Readonly<T> {
  return Object.freeze([...values]) as unknown as Readonly<T>;
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function readPath(record: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (!isRecord(current)) return undefined;
    return current[segment];
  }, record);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
